import { runUploadTransport } from '../utils/uploadTransport';
import { createPresignedUploadCoordinator } from '../utils/presignedUpload';
import { uploadResponseError, blocksUploadFallback, createUploadPauseGate, isUploadPaused } from '../utils/uploadPause';
import UploadPanelView from './UploadPanelView';
import { getPrivateCacheScope, isCurrentPrivateCacheScope, subscribePrivateCacheScope } from '../utils/privateCacheScope';
import { supportsMobileCapture } from '../utils/mobileCapture';
import { buildUploadQueue, uploadFailureEntry, mergeTransferProgress } from '../utils/uploadBatchState';
import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { FiMinimize2, FiMaximize2, FiAlertTriangle, FiSend } from 'react-icons/fi';
import PuziakBlock from './PuziakBlock';
import AlbumSelectorDropdown from './AlbumSelectorDropdown';
import { showToast } from './NotificationTray';
import { buildUploadUrl, resolveUploadTarget, withAuthHeaders, getAuthToken } from '../utils/uploadRegion';
import { useUpload } from '../contexts/UploadContext';
import useUploadDialog from '../hooks/useUploadDialog';
import {
  getProcessingStatus,
  isProcessingLike,
  isRecoverableProcessingFailure,
  isTerminalProcessingFailure,
  normalizeProcessingError
} from '../utils/processingState';
import { RAW_OR_PRO_EXTENSIONS, SUPPORTED_EXTENSIONS, SUPPORTED_EXTENSIONS_TEXT, UPLOAD_FILE_ACCEPT } from '../utils/supportedExtensions';
import { classifyFiles, readEntriesToFiles } from '../utils/fileIntake';

// Error categorization for user-friendly messages
const ERROR_CATEGORIES = {
  unsupported_format: {
    title: 'Unsupported format',
    message: 'This RAW format is not supported yet',
    action: 'Convert to DNG using Adobe DNG Converter',
    retryable: false
  },
  corrupted_file: {
    title: 'File corrupted',
    message: 'The file appears damaged or incomplete',
    action: 'Try re-exporting from your camera',
    retryable: false
  },
  processing_timeout: {
    title: 'Timeout',
    message: 'Processing took too long',
    action: 'Retry - often works on second attempt',
    retryable: true
  },
  system_error: {
    title: 'System error',
    message: 'Something went wrong on our end',
    action: 'Try again or report this issue',
    retryable: true
  }
};

function categorizeError(errorInput) {
  const normalized = normalizeProcessingError(errorInput);
  const explicitCategory = String(normalized?.category || '').toLowerCase();
  if (explicitCategory && ERROR_CATEGORIES[explicitCategory]) {
    return explicitCategory;
  }
  const lower = String(normalized?.message || errorInput || '').toLowerCase();
  if (lower.includes('unsupported') || lower.includes('unknown format')) return 'unsupported_format';
  if (lower.includes('corrupt') || lower.includes('invalid') || lower.includes('truncated')) return 'corrupted_file';
  if (lower.includes('timeout') || lower.includes('timed out')) return 'processing_timeout';
  return 'system_error';
}

// Performance tuning constants
const UPLOAD_MODES = {
  fast: {
    concurrent: 15,
    label: 'Fast',
    description: 'Upload many files at once. Best for fast internet connections.',
    icon: '⚡'
  },
  balanced: {
    concurrent: 4,
    label: 'Balanced',
    description: 'Upload a few files at a time. Good for most connections.',
    icon: '⚖️'
  },
  stable: {
    concurrent: 1,
    label: 'Stable',
    description: 'Upload one file at a time. Best for slow or unstable connections.',
    icon: '🐢'
  }
};
const DEFAULT_UPLOAD_MODE = 'fast';
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_BASE_DELAY = 1000; // 1s, 2s, 4s exponential backoff
// Enable presigned URL uploads for direct browser → S3 uploads (faster)
const USE_PRESIGNED_UPLOAD = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_PRESIGNED_UPLOAD !== 'false');
const MAX_VISIBLE_THUMBNAILS = 100; // Only render first 100 thumbnails
const THUMBNAIL_BATCH_SIZE = 10; // Process thumbnails in batches
const THUMBNAIL_DELAY = 16; // ~60fps
const MAX_FILE_MB = Number((typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_MAX_UPLOAD_MB) || 250);
const MAX_FILE_BYTES = Math.max(1, MAX_FILE_MB) * 1024 * 1024;
const MIN_RAW_UPLOAD_BYTES = 4096;

function isSuspiciousRawPlaceholder(file, extension) {
  if (!file || !RAW_OR_PRO_EXTENSIONS.has(extension)) return false;
  const bytes = Number(file.size || 0);
  if (bytes > 0 && bytes < MIN_RAW_UPLOAD_BYTES) return true;
  const mime = String(file.type || '').toLowerCase();
  if (!mime) return false;
  return mime.includes('svg') || mime.includes('xml') || mime === 'text/html' || mime === 'text/plain';
}

const EMPTY_SELECTION = [];
export default function BatchUploader(props) {
  const initialScope = useRef(getPrivateCacheScope());
  const [scope, setScope] = useState(getPrivateCacheScope);
  useEffect(() => subscribePrivateCacheScope(setScope), []);
  return <UploadSession key={scope.generation} {...props} initialFiles={scope.generation === initialScope.current.generation ? props.initialFiles : EMPTY_SELECTION} />;
}

function UploadSession({ onUploadComplete, onRequestClose, initialFiles = [], albumId = null, archiveFolderId = null, libraryScope = 'library', vaultCollectionId = null }) {
  const identityRef = useRef(getPrivateCacheScope());
  const batchStartGuardRef = useRef(false);
  const presignedCoordinator = useRef(createPresignedUploadCoordinator());
  const uploadPause = useRef(createUploadPauseGate());
  const activeSessionRef = useRef(true);
  const requestsRef = useRef(new Set());
  const fetchAbortRef = useRef(new AbortController());
  const sessionCurrent = () => activeSessionRef.current && isCurrentPrivateCacheScope(identityRef.current);
  const assertSession = () => {
    if (!sessionCurrent()) throw Object.assign(new Error('Upload session ended'), { name: 'AbortError', status: 401 });
  };
  useEffect(() => {
    activeSessionRef.current = true;
    if (fetchAbortRef.current.signal.aborted) fetchAbortRef.current = new AbortController();
    const stop = () => {
      activeSessionRef.current = false;
      fetchAbortRef.current.abort();
      for (const xhr of requestsRef.current) xhr.abort();
      requestsRef.current.clear();
    };
    const unsubscribe = subscribePrivateCacheScope(stop);
    return () => { unsubscribe(); stop(); };
  }, []);
  // Global upload context for background uploads
  const uploadContext = useUpload();

  const [files, setFiles] = useState(initialFiles);
  const [uploading, setUploading] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [progress, setProgress] = useState({});
  const [autoClear, setAutoClear] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const fileInputRef = useRef();
  const captureInputRef = useRef();
  const [canCapture, setCanCapture] = useState(false);
  useEffect(() => {
    const media = window.matchMedia('(pointer: coarse)');
    const update = () => setCanCapture(supportsMobileCapture(navigator, media.matches, 'capture' in document.createElement('input')));
    update(); media.addEventListener?.('change', update);
    return () => media.removeEventListener?.('change', update);
  }, []);
  const folderInputRef = useRef();
  const dropzoneRef = useRef();
  const [dragActive, setDragActive] = useState(false);
  const [blockedFiles, setBlockedFiles] = useState([]);
  const [showPuziakBlock, setShowPuziakBlock] = useState(false);
  const [oversizedFiles, setOversizedFiles] = useState([]);
  const [previews, setPreviews] = useState(new Map()); // Use Map for better performance
  const uploadStartRef = useRef(null);
  const thumbnailQueueRef = useRef([]);
  const processingRef = useRef(false);
  const [showAllThumbnails, setShowAllThumbnails] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [uploadTarget, setUploadTarget] = useState({ baseUrl: '', fallbacks: [], source: 'default' });
  const [activeTab, setActiveTab] = useState('active');
  const [processingQueue, setProcessingQueue] = useState(null);
  const [authExpired, setAuthExpired] = useState(false);
  const [duplicates, setDuplicates] = useState([]); // [{index, existingId, existingFilename}]
  const [showAddMore, setShowAddMore] = useState(false);
  const [selectedAlbumId, setSelectedAlbumId] = useState(albumId);
  const [hideFromLibrary, setHideFromLibrary] = useState(false);
  const keepAliveRef = useRef(null);
  const [bugReportData, setBugReportData] = useState(null); // For bug report modal
  const [submittingReport, setSubmittingReport] = useState(false);
  const [exitingItems, setExitingItems] = useState(new Set()); // Track items animating out
  const [completedCollapsed, setCompletedCollapsed] = useState(true); // Collapse completed section by default
  const [fileListCollapsed, setFileListCollapsed] = useState(true); // Collapse file list by default
  const pendingProgressRef = useRef({}); // Buffer for batched progress updates
  const progressFlushTimerRef = useRef(null);
  const [uploadMode, setUploadMode] = useState(() => {
    const saved = localStorage.getItem('uploadMode');
    return saved && UPLOAD_MODES[saved] ? saved : DEFAULT_UPLOAD_MODE;
  });
  const [showModeSelector, setShowModeSelector] = useState(false);
  const modeSelectorRef = useRef(null);
  // Persist upload mode preference
  const handleUploadModeChange = useCallback((mode) => {
    setUploadMode(mode);
    localStorage.setItem('uploadMode', mode);
    setShowModeSelector(false);
  }, []);

  // Close mode selector when clicking outside
  useEffect(() => {
    if (!showModeSelector) return;
    const handleClickOutside = (e) => {
      if (modeSelectorRef.current && !modeSelectorRef.current.contains(e.target)) {
        setShowModeSelector(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showModeSelector]);

  // Batched progress updates - reduces UI jitter during fast uploads
  const PROGRESS_BATCH_INTERVAL = 150; // ms - flush progress updates every 150ms

  const flushProgress = useCallback(() => {
    const pending = pendingProgressRef.current;
    if (Object.keys(pending).length > 0) {
      setProgress(prev => mergeTransferProgress(prev, pending));
      pendingProgressRef.current = {};
    }
  }, []);

  const batchedSetProgress = useCallback((index, update) => {
    pendingProgressRef.current[index] = typeof update === 'function'
      ? update(pendingProgressRef.current[index] || {})
      : update;

    // Schedule flush if not already scheduled
    if (!progressFlushTimerRef.current) {
      progressFlushTimerRef.current = setTimeout(() => {
        flushProgress();
        progressFlushTimerRef.current = null;
      }, PROGRESS_BATCH_INTERVAL);
    }
  }, [flushProgress]);

  // Flush pending progress on unmount
  useEffect(() => {
    return () => {
      if (progressFlushTimerRef.current) {
        clearTimeout(progressFlushTimerRef.current);
        flushProgress();
      }
    };
  }, [flushProgress]);

  // Submit bug report for failed processing
  const submitBugReport = useCallback(async (data) => {
    setSubmittingReport(true);
    try {
      const errorInfo = ERROR_CATEGORIES[data.errorCategory] || ERROR_CATEGORIES.system_error;
      const description = [
        `Processing Failure: ${errorInfo.title}`,
        `File: ${data.filename}`,
        `Image ID: ${data.imageId || 'N/A'}`,
        `Error: ${data.error || 'Unknown error'}`,
        data.additionalInfo ? `\nUser notes: ${data.additionalInfo}` : ''
      ].filter(Boolean).join('\n');

      const reportPayload = {
        category: 'processing_failure',
        description,
        userAgent: navigator.userAgent,
        currentUrl: window.location.href,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        timestamp: new Date().toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        pathname: window.location.pathname
      };
      const res = await fetch('/api/bug-report', {
        method: 'POST',
        credentials: 'include',
        headers: withAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(reportPayload)
      });
      if (res.ok) {
        showToast('Bug report submitted', 'Thank you! We\'ll investigate this issue.', 'success');
        setBugReportData(null);
      } else {
        showToast('Failed to submit report', 'Please try again or email support@dailey.cloud', 'error');
      }
    } catch (err) {
      showToast('Failed to submit report', 'Please try again or email support@dailey.cloud', 'error');
    } finally {
      setSubmittingReport(false);
    }
  }, []);

  const toProcessingState = useCallback((entry) => ({
    processing_status: entry?.processingStatus || entry?.status || '',
    processing_error: entry?.processingError || entry?.processingErrorMessage || null
  }), []);

  const isRecoverableEntryFailure = useCallback((entry) => {
    if (!entry || (!entry.needsProcessing && !entry.imageId)) return false;
    return isRecoverableProcessingFailure(toProcessingState(entry));
  }, [toProcessingState]);

  const isTerminalEntryFailure = useCallback((entry) => {
    if (!entry || (!entry.needsProcessing && !entry.imageId)) return false;
    return isTerminalProcessingFailure(toProcessingState(entry));
  }, [toProcessingState]);

  const isProcessingLikeEntry = useCallback((entry) => {
    if (!entry || (!entry.needsProcessing && !entry.imageId)) return false;
    if (String(entry?.status || '').toLowerCase() === 'processing') return true;
    return isProcessingLike(toProcessingState(entry));
  }, [toProcessingState]);

  // Subscribe to the stable action, not the context value changed by this effect.
  const syncUploadState = uploadContext?.syncState;
  // Sync upload state with global context for background upload indicator
  useEffect(() => {
    if (syncUploadState) {
      // Compute stats for context
      const progressList = Object.values(progress);
      const completedCount = progressList.filter(p => p?.status === 'complete').length;
      const processingCount = progressList.filter((p) => (
        p?.status === 'processing' || isRecoverableEntryFailure(p)
      )).length;
      const failedCount = progressList.filter((p) => (
        (p?.status === 'error' && !isUploadedToS3(p)) || isTerminalEntryFailure(p)
      )).length;
      const uploadedCount = completedCount + processingCount;
      const isComplete = completedCount + failedCount >= files.length && files.length > 0 && !uploading && processingCount === 0;

      // Update context with current state
      syncUploadState({
        totalFiles: files.length,
        uploadedCount,
        completedCount,
        processingCount,
        failedCount,
        uploading,
        isComplete,
        attentionCount: failedCount
      });
    }
  }, [files.length, progress, uploading, syncUploadState, isRecoverableEntryFailure, isTerminalEntryFailure]); // syncState is stable

  // Handle minimize - minimize to indicator, modal stays mounted but hidden
  const handleMinimize = useCallback(() => {
    if (uploadContext) {
      uploadContext.minimizePanel?.();
    }
    setIsMinimized(true);
    // Don't call onRequestClose - the parent will hide the modal via CSS
    // but keep it mounted so uploads continue
  }, [uploadContext]);

  const uploadTargets = useMemo(() => {
    const targets = [];
    if (uploadTarget?.baseUrl) targets.push(uploadTarget.baseUrl);
    if (Array.isArray(uploadTarget?.fallbacks)) targets.push(...uploadTarget.fallbacks);
    targets.push('');
    return Array.from(new Set(targets.filter((item) => item !== null && item !== undefined)));
  }, [uploadTarget]);

  useEffect(() => {
    let active = true;
    resolveUploadTarget().then((target) => {
      if (active && target) setUploadTarget(target);
    });
    return () => {
      active = false;
    };
  }, []);

  const uploadFetch = useCallback(async (path, options = {}, baseUrl = uploadTarget?.baseUrl || '') => {
    assertSession();
    const headers = withAuthHeaders(options.headers || {});
    const response = await fetch(buildUploadUrl(path, baseUrl), {
      credentials: 'include',
      ...options,
      headers,
      signal: fetchAbortRef.current.signal
    });
    assertSession();
    return response;
  }, [uploadTarget]);

  const applyAuthHeader = (xhr) => {
    const token = getAuthToken();
    if (token) {
      try {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      } catch {}
    }
  };

  const shouldFallback = (err) => {
    if (blocksUploadFallback(err)) return false;
    const status = err?.status;
    if (!status) return true;
    if (status === 401) {
      // Auth expired - don't retry, show error
      setAuthExpired(true);
      return false;
    }
    if (status >= 500) return true;
    if (status === 429) return true;
    return false;
  };

  // Keep auth session alive during uploads - ping every 2 minutes
  const startKeepAlive = useCallback(() => {
    if (keepAliveRef.current) return;
    const ping = async () => {
      try {
        await fetch('/api/auth/me', {
          credentials: 'include',
          headers: withAuthHeaders()
        });
      } catch {}
    };
    ping(); // Immediate ping
    keepAliveRef.current = setInterval(ping, 2 * 60 * 1000); // Every 2 minutes
  }, []);

  const stopKeepAlive = useCallback(() => {
    if (keepAliveRef.current) {
      clearInterval(keepAliveRef.current);
      keepAliveRef.current = null;
    }
  }, []);

  // Cleanup keep-alive on unmount
  useEffect(() => {
    return () => stopKeepAlive();
  }, [stopKeepAlive]);
  
  // Handle initial files when they change
  React.useEffect(() => {
    if (initialFiles.length > 0 && !uploading && !initializing && !Object.keys(progress).length) {
      setFiles(initialFiles);
    }
  }, [initialFiles]);

  const ownedPreviewUrls = useRef(new Set());
  const displayedPreviewUrls = useRef(new Set());
  const createOwnedPreview = value => {
    if (!sessionCurrent()) return null;
    const url = URL.createObjectURL(value); ownedPreviewUrls.current.add(url); return url;
  };
  const releaseOwnedPreview = url => {
    if (ownedPreviewUrls.current.delete(url)) URL.revokeObjectURL(url);
  };
  useEffect(() => {
    const visible = new Set(previews.values());
    for (const url of displayedPreviewUrls.current) if (!visible.has(url)) releaseOwnedPreview(url);
    displayedPreviewUrls.current = visible;
  }, [previews]);
  // Track ownership independently of render closures and replaced preview maps.
  const cleanupPreviews = useCallback(() => {
    for (const url of ownedPreviewUrls.current) URL.revokeObjectURL(url);
    ownedPreviewUrls.current.clear();
  }, []);

  // Detect RAW-like extensions that browsers cannot preview reliably
  const isRawExt = useCallback((name = '') => {
    const lower = String(name).toLowerCase();
    return ['.cr2','.cr3','.nef','.arw','.raf','.orf','.rw2','.srw','.x3f','.pef','.raw','.dng'].some(ext => lower.endsWith(ext));
  }, []);

  // Create EXIF-aware preview when possible; skip RAW
  const makePreviewUrl = useCallback(async (file) => {
    try {
      if (!file) return null;
      if (isRawExt(file.name)) return null;
      if (typeof createImageBitmap === 'function') {
        const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
        const maxW = 320;
        const scale = Math.min(1, maxW / (bmp.width || 1));
        const w = Math.max(1, Math.round((bmp.width || 1) * scale));
        const h = Math.max(1, Math.round((bmp.height || 1) * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(bmp, 0, 0, w, h);
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
        if (blob) return createOwnedPreview(blob);
      }
      return createOwnedPreview(file);
    } catch (e) {
      return null;
    }
  }, [isRawExt]);

  // Process thumbnail queue in batches to avoid blocking UI
  const processThumbnailQueue = useCallback(async () => {
    if (!sessionCurrent() || processingRef.current || thumbnailQueueRef.current.length === 0) return;
    
    processingRef.current = true;
    const batch = thumbnailQueueRef.current.splice(0, THUMBNAIL_BATCH_SIZE);
    
    const newPreviews = new Map();
    
    for (const { file, index } of batch) {
      if (file && file.type && file.type.startsWith('image/')) {
        const url = await makePreviewUrl(file);
        if (url) newPreviews.set(index, url);
      }
    }
    
    setPreviews(prev => {
      const updated = new Map(prev);
      newPreviews.forEach((url, index) => {
        // Clean up old URL if exists
        const oldUrl = updated.get(index);
        if (oldUrl) URL.revokeObjectURL(oldUrl);
        updated.set(index, url);
      });
      return updated;
    });
    
    processingRef.current = false;
    
    // Process next batch after delay
    if (thumbnailQueueRef.current.length > 0) {
      setTimeout(processThumbnailQueue, THUMBNAIL_DELAY);
    }
  }, [makePreviewUrl]);

  // Queue thumbnails for processing when files change
  React.useEffect(() => {
    // Clear old previews
    cleanupPreviews();
    setPreviews(new Map());
    
    // Queue new thumbnails (limit to visible ones initially)
    const limit = showAllThumbnails ? files.length : Math.min(MAX_VISIBLE_THUMBNAILS, files.length);
    const newQueue = [];
    
    for (let i = 0; i < limit; i++) {
      if (files[i]) {
        newQueue.push({ file: files[i], index: i });
      }
    }
    
    thumbnailQueueRef.current = newQueue;
    processThumbnailQueue();
    
    return cleanupPreviews;
  }, [files, showAllThumbnails]);

  // Mark start time when uploading begins (for ETA)
  React.useEffect(() => {
    if (uploading && !uploadStartRef.current) {
      uploadStartRef.current = Date.now();
    }
    if (!uploading) {
      uploadStartRef.current = null;
    }
  }, [uploading]);

  function handleDrag(e) {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }

  // Shared intake for the folder picker, drag-drop, and file picker. OS/system
  // files (.DS_Store, Thumbs.db, ._AppleDouble, __MACOSX/…) are dropped silently
  // so a photographer dragging a whole shoot folder never sees junk warnings.
  function ingestFiles(incoming) {
    setAutoClear(false);
    const { valid, invalid, oversize, suspicious, junk } = classifyFiles(incoming, {
      maxBytes: MAX_FILE_BYTES,
      minRawBytes: MIN_RAW_UPLOAD_BYTES,
    });

    if (junk.length > 0) {
      console.log(`[upload] skipped ${junk.length} system file(s) (e.g. .DS_Store, Thumbs.db)`);
    }
    if (invalid.length > 0) {
      setBlockedFiles(invalid);
      setShowPuziakBlock(true);
    }
    if (oversize.length > 0) {
      setOversizedFiles(prev => [...prev, ...oversize]);
      showToast(`Skipped ${oversize.length} file(s) over ${MAX_FILE_MB}MB`, 'error');
    }
    if (suspicious.length > 0) {
      showToast(
        `Skipped ${suspicious.length} file(s): looks like a preview placeholder, not the original RAW/HEIC file`,
        'error'
      );
    }
    if (valid.length > 0) {
      setFiles(prev => [...prev, ...valid]);
    }
    return { valid, invalid, oversize, suspicious, junk };
  }

  async function handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const dt = e.dataTransfer;
    if (!dt) return;

    // Capture directory entries SYNCHRONOUSLY, webkitGetAsEntry() is only valid
    // during the drop event. If any dropped item is a folder, recurse the whole
    // tree into files (drop a shoot folder); otherwise use the flat file list.
    let entries = [];
    try {
      const items = dt.items ? Array.from(dt.items) : [];
      entries = items
        .map(it => (typeof it.webkitGetAsEntry === 'function' ? it.webkitGetAsEntry() : null))
        .filter(Boolean);
    } catch { entries = []; }

    if (entries.length > 0 && entries.some(en => en.isDirectory)) {
      let files = [];
      try {
        files = await readEntriesToFiles(entries);
      } catch (err) {
        console.warn('[upload] folder read failed, falling back to flat files:', err?.message);
        files = dt.files ? Array.from(dt.files) : [];
      }
      const result = ingestFiles(files);
      if (files.length > 0 && result.valid.length === 0 && result.invalid.length === 0
          && result.oversize.length === 0 && result.suspicious.length === 0) {
        showToast('That folder had no supported photos in it', 'error');
      }
      return;
    }

    if (dt.files && dt.files.length > 0) {
      ingestFiles(Array.from(dt.files));
    }
  }

  function handleFileSelect(e) {
    if (e.target.files && e.target.files.length > 0) {
      ingestFiles(Array.from(e.target.files));
    }
    // Reset so the same folder/file can be re-selected immediately.
    try { e.target.value = ''; } catch { /* ignore */ }
  }

  function removeFile(index) {
    const reindex = entries => Object.fromEntries(Object.entries(entries)
      .filter(([key]) => Number(key) !== index)
      .map(([key,value]) => [Number(key) > index ? Number(key)-1 : Number(key), value]));
    setProgress(reindex);
    pendingProgressRef.current = reindex(pendingProgressRef.current);

    // Clean up preview if exists
    const url = previews.get(index);
    if (url) {
      URL.revokeObjectURL(url);
      setPreviews(prev => {
        const updated = new Map(prev);
        updated.delete(index);
        return updated;
      });
    }
    
    setFiles(prev => prev.filter((_, i) => i !== index));
    
    // Rebuild previews map with new indices
    setPreviews(prev => {
      const updated = new Map();
      prev.forEach((url, oldIndex) => {
        if (oldIndex < index) {
          updated.set(oldIndex, url);
        } else if (oldIndex > index) {
          updated.set(oldIndex - 1, url);
        }
      });
      return updated;
    });
  }

  function clearFiles() {
    pendingProgressRef.current = {};
    cleanupPreviews();
    setFiles([]);
    setProgress({});
    setOversizedFiles([]);
    setPreviews(new Map());
    thumbnailQueueRef.current = [];
    setAutoClear(false);
  }

  // Remove a failed or stuck file from the list
  async function removeFailedFile(index) {
    const entry = progress[index];
    // Allow removing: error status, failed processing, or stuck processing (when not uploading)
    const canRemove = entry && (
      entry.status === 'error' ||
      (entry.status === 'processing' && entry.processingStatus === 'failed') ||
      (entry.status === 'processing' && !uploading)
    );
    if (!canRemove) {
      return;
    }

    // If the file was uploaded (has imageId), delete from server too
    if (entry.imageId) {
      try {
        await uploadFetch(`/api/images/${entry.imageId}`, { method: 'DELETE' }, '');
        // Trigger refresh after deletion
        if (onUploadComplete) {
          onUploadComplete({ deleted: 1, refresh: true });
        }
      } catch (err) {
        console.error('Failed to delete image from server:', err);
      }
    }

    // Clean up preview if exists
    const url = previews.get(index);
    if (url && String(url).startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }

    // Remove from files array
    setFiles(prev => prev.filter((_, i) => i !== index));

    // Rebuild previews and progress maps with new indices
    setPreviews(prev => {
      const updated = new Map();
      prev.forEach((url, oldIndex) => {
        if (oldIndex < index) {
          updated.set(oldIndex, url);
        } else if (oldIndex > index) {
          updated.set(oldIndex - 1, url);
        }
      });
      return updated;
    });

    setProgress(prev => {
      const updated = {};
      Object.entries(prev).forEach(([key, value]) => {
        const oldIndex = Number(key);
        if (oldIndex < index) {
          updated[oldIndex] = value;
        } else if (oldIndex > index) {
          updated[oldIndex - 1] = value;
        }
      });
      return updated;
    });
  }

  // Retry processing for a failed image (server-side reprocess)
  async function retryProcessing(imageId) {
    if (!imageId) return;
    try {
      const res = await uploadFetch(`/api/images/${imageId}/reprocess`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (res.ok) {
        showToast('Reprocessing started', 'success');
        // Update the progress entry to show it's requeued
        setProgress(prev => {
          const next = { ...prev };
          Object.entries(prev).forEach(([key, entry]) => {
            if (entry?.imageId === imageId) {
              next[key] = {
                ...entry,
                status: 'processing',
                processingStatus: 'queued'
              };
            }
          });
          return next;
        });
      } else {
        const data = await res.json().catch(() => ({}));
        showToast(data.error || 'Retry failed', 'error');
      }
    } catch (err) {
      showToast('Retry failed: ' + (err.message || 'Network error'), 'error');
    }
  }

  // Presigned URL upload: browser → S3 directly (bypasses server for file transfer)
  async function uploadFilePresignedWithBase(file, index, statusPrefix, uploadedCount, baseUrl) {
    const scope = JSON.stringify([identityRef.current, baseUrl, vaultCollectionId, selectedAlbumId, archiveFolderId, libraryScope, hideFromLibrary]);
    const completed = await presignedCoordinator.current.run(file, scope, {
    initiate: async () => {
    assertSession();
    // Step 1: Request presigned URL from server
    const presignedEndpoint = vaultCollectionId
      ? `/api/vault/collections/${vaultCollectionId}/upload/initiate`
      : '/api/images/presigned';
    const presignedBody = vaultCollectionId
      ? { filename: file.name, file_size_bytes: file.size, mime_type: file.type || 'application/octet-stream' }
      : { filename: file.name, contentType: file.type || 'application/octet-stream', size: file.size, library_scope: libraryScope };
    const presignedRes = await uploadFetch(presignedEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(presignedBody)
    }, baseUrl);

    if (!presignedRes.ok) {
      const err = await presignedRes.json().catch(() => ({}));
      throw uploadResponseError(presignedRes.status, err, presignedRes.headers.get('Retry-After'));
    }

    const presignedData = await presignedRes.json();
    const uploadUrl = presignedData.uploadUrl || presignedData.upload_url;
    const confirmToken = presignedData.confirmToken || presignedData.confirm_token;
    const safeName = presignedData.filename || file.name;

    return { uploadUrl, confirmToken, safeName };
    },
    transfer: async ({uploadUrl}) => {
    // Step 2: Upload directly to S3 using presigned URL
    await new Promise((resolve, reject) => {
      assertSession();
      const xhr = new XMLHttpRequest();
      requestsRef.current.add(xhr);
      xhr.addEventListener('loadend', () => requestsRef.current.delete(xhr));

      xhr.upload.addEventListener('progress', (e) => {
        if (!sessionCurrent()) return;
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100);
          // When upload hits 100%, immediately mark as confirming (bytes sent, waiting for S3 response)
          const newStatus = percent === 100 ? 'confirming' : statusPrefix;
          // Use batched updates for smoother UI during fast uploads
          batchedSetProgress(index, { status: newStatus, percent });
        }
      });

      xhr.addEventListener('load', () => {
        if (!sessionCurrent()) { reject(Object.assign(new Error('Upload session ended'), {name:'AbortError',status:401})); return; }
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`S3 upload failed: ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => reject(new Error('S3 network error')));
      xhr.addEventListener('abort', () => reject(new Error('S3 upload aborted')));

      // Presigned URLs contain auth in the signature - don't send credentials
      xhr.withCredentials = false;
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
      xhr.send(file);
    });

    },
    confirm: async ({confirmToken}) => {
    assertSession();
    // Step 3: Confirm upload with server (creates DB record, triggers variant generation)
    const confirmEndpoint = vaultCollectionId
      ? `/api/vault/collections/${vaultCollectionId}/upload/complete`
      : '/api/images/confirm';
    const confirmBody = vaultCollectionId
      ? { confirm_token: confirmToken }
      : { confirmToken, albumId: selectedAlbumId, archiveFolderId, library_scope: libraryScope, hidden_from_library: hideFromLibrary ? '1' : '0' };
    const confirmRes = await uploadFetch(confirmEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(confirmBody)
    }, baseUrl);

    if (!confirmRes.ok) {
      const err = await confirmRes.json().catch(() => ({}));
      throw uploadResponseError(confirmRes.status, err, confirmRes.headers.get('Retry-After'));
    }

    return confirmRes.json();
    }
    });
    const resp = completed.response;
    const safeName = completed.handle.safeName;
    assertSession();
    const responseId = resp?.id || null;
    const responseFilename = resp?.filename || safeName;
    const processingStatus = String(resp?.processing_status || '').toLowerCase();
    const assumeProcessing = !processingStatus && isRawExt(file?.name);
    const processingFailure = processingStatus === 'failed' && !isRecoverableProcessingFailure(resp);
    const isProcessing = isProcessingLike(resp) || assumeProcessing;
    const nextProcessingStatus = processingStatus || (isProcessing ? 'queued' : 'ready');

    setProgress(prev => ({
      ...prev,
      [index]: {
        status: processingFailure ? 'error' : isProcessing ? 'processing' : 'complete',
        percent: 100,
        processingStatus: nextProcessingStatus,
        processingError: normalizeProcessingError(resp.processing_error || resp.error),
        needsProcessing: isProcessing,
        imageId: responseId,
        filename: responseFilename
      }
    }));

    uploadedCount.success++;
    if (isProcessing) uploadedCount.processing++;

    // Update preview if ready
    try {
      if (responseFilename && !isProcessing && !processingFailure) {
        const token = window?.localStorage?.getItem('auth_token');
        const variant = `/media/variants/sm/${responseFilename.replace(/\.[^.]+$/, '.jpg')}`;
        setPreviews(prev => {
          const updated = new Map(prev);
          if (!updated.get(index) || isRawExt(file?.name)) {
            const old = updated.get(index);
            if (old && String(old).startsWith('blob:')) URL.revokeObjectURL(old);
            updated.set(index, variant);
          }
          return updated;
        });
      }
    } catch (e) {}
  }

  async function uploadFilePresigned(file, index, statusPrefix, uploadedCount) {
    let lastError;
    for (const baseUrl of uploadTargets) {
      try {
        await uploadFilePresignedWithBase(file, index, statusPrefix, uploadedCount, baseUrl);
        return;
      } catch (err) {
        lastError = err;
        if (!shouldFallback(err)) break;
      }
    }
    throw lastError;
  }

  // Traditional upload: browser → server → S3 (fallback)
  async function uploadFileTraditionalWithBase(file, index, statusPrefix, uploadedCount, baseUrl) {
    const formData = new FormData();
    formData.append('file', file);
    if (selectedAlbumId) {
      formData.append('albumId', selectedAlbumId);
    }
    if (archiveFolderId) {
      formData.append('archiveFolderId', archiveFolderId);
    }
    if (libraryScope) {
      formData.append('library_scope', libraryScope);
    }
    if (hideFromLibrary) {
      formData.append('hidden_from_library', '1');
    }

    return new Promise((resolve, reject) => {
      assertSession();
      const xhr = new XMLHttpRequest();
      requestsRef.current.add(xhr);
      xhr.addEventListener('loadend', () => requestsRef.current.delete(xhr));
      let progressSeen = false;
      const waitingTimer = setTimeout(() => {
        if (progressSeen) return;
        setProgress(prev => {
          const current = prev[index] || {};
          if (current.status) {
            const status = String(current.status);
            if (!status.startsWith('uploading') && !status.startsWith('retry')) return prev;
          }
          return { ...prev, [index]: { ...current, status: 'waiting for server', percent: current.percent || 0 } };
        });
      }, 2000);

      xhr.upload.addEventListener('progress', (e) => {
        if (!sessionCurrent()) return;
        if (e.lengthComputable) {
          progressSeen = true;
          clearTimeout(waitingTimer);
          const percent = Math.round((e.loaded / e.total) * 100);
          // When upload hits 100%, immediately mark as confirming (bytes sent, waiting for server response)
          const newStatus = percent === 100 ? 'confirming' : statusPrefix;
          // Use batched updates for smoother UI during fast uploads
          batchedSetProgress(index, { status: newStatus, percent });
        }
      });

      xhr.addEventListener('load', () => {
        if (!sessionCurrent()) { reject(Object.assign(new Error('Upload session ended'), {name:'AbortError',status:401})); return; }
        clearTimeout(waitingTimer);
        if (xhr.status >= 200 && xhr.status < 300) {
          let resp = {};
          try {
            resp = JSON.parse(xhr.responseText || '{}');
          } catch {}
          const responseId = resp?.id || null;
          const responseFilename = resp?.filename || null;
          const processingStatus = String(resp?.processing_status || '').toLowerCase();
          const assumeProcessing = !processingStatus && isRawExt(file?.name);
          const processingFailure = processingStatus === 'failed' && !isRecoverableProcessingFailure(resp);
    const isProcessing = isProcessingLike(resp) || assumeProcessing;
          const nextProcessingStatus = processingStatus || (isProcessing ? 'queued' : 'ready');
          setProgress(prev => ({
            ...prev,
            [index]: {
              status: processingFailure ? 'error' : isProcessing ? 'processing' : 'complete',
              percent: 100,
              processingStatus: nextProcessingStatus,
        processingError: normalizeProcessingError(resp.processing_error || resp.error),
              needsProcessing: isProcessing,
              imageId: responseId,
              filename: responseFilename
            }
          }));
          uploadedCount.success++;
          if (isProcessing) uploadedCount.processing++;
          try {
            if (responseFilename && !isProcessing && !processingFailure) {
              const token = window?.localStorage?.getItem('auth_token');
              const variant = `/media/variants/sm/${responseFilename.replace(/\.[^.]+$/, '.jpg')}`;
              setPreviews(prev => {
                const updated = new Map(prev);
                if (!updated.get(index) || isRawExt(file?.name)) {
                  const old = updated.get(index);
                  if (old && String(old).startsWith('blob:')) URL.revokeObjectURL(old);
                  updated.set(index, variant);
                }
                return updated;
              });
            }
          } catch (e) {}
          resolve(xhr);
        } else {
          if (xhr.status === 429) {
            try {
              const msg = JSON.parse(xhr.responseText || '{}')?.error;
              showToast(msg || 'Server is busy. Your upload will retry shortly.', '', 'info');
            } catch {
              showToast('Server is busy. Your upload will retry shortly.', '', 'info');
            }
          }
          let body = {};
          try { body = JSON.parse(xhr.responseText || '{}'); } catch {}
          reject(uploadResponseError(xhr.status, body, xhr.getResponseHeader('Retry-After')));
        }
      });

      xhr.addEventListener('error', () => {
        clearTimeout(waitingTimer);
        const error = new Error('Network error');
        error.status = 0;
        reject(error);
      });
      xhr.addEventListener('abort', () => {
        clearTimeout(waitingTimer);
        const error = new Error('Upload aborted');
        error.status = 0;
        reject(error);
      });

      xhr.withCredentials = true;
      xhr.open('POST', buildUploadUrl('/api/images', baseUrl));
      applyAuthHeader(xhr);
      xhr.withCredentials = true;

      // Handle album addition after upload (for traditional flow)
      if (selectedAlbumId) {
        xhr.addEventListener('load', async () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const response = JSON.parse(xhr.responseText);
              if (response.id) {
                await uploadFetch(`/api/albums/${selectedAlbumId}/images`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ image_id: response.id })
                }, baseUrl);
              }
            } catch (err) {
              console.error('Failed to add image to album:', err);
            }
          }
        });
      }

      xhr.send(formData);
    });
  }

  async function uploadFileTraditional(file, index, statusPrefix, uploadedCount) {
    let lastError;
    for (const baseUrl of uploadTargets) {
      try {
        await uploadFileTraditionalWithBase(file, index, statusPrefix, uploadedCount, baseUrl);
        return;
      } catch (err) {
        lastError = err;
        if (!shouldFallback(err)) break;
      }
    }
    throw lastError;
  }

  async function uploadFiles(onlyIndex = null) {
    // State updates render later; a synchronous guard also covers duplicate
    // callbacks in the same event turn without starting a second batch.
    if (batchStartGuardRef.current) return;
    batchStartGuardRef.current = true;
    try {
      return await runUploadBatch(onlyIndex);
    } finally {
      batchStartGuardRef.current = false;
    }
  }

  async function runUploadBatch(onlyIndex = null) {
    if (files.length === 0) return;
    if (!uploadPause.current.start()) {
      showToast(`Uploads paused. Retry after ${new Date(uploadPause.current.paused.retryAt).toLocaleTimeString()}.`, 'info');
      return;
    }
    if (uploading || initializing) return;
    const hasInFlightProcessing = Object.values(progress).some((entry) => entry?.status === 'processing');
    if (hasInFlightProcessing) return;

    setUploading(true);
    setInitializing(true); // Show initializing state
    setAutoClear(false);
    setActiveTab('active');
    setAuthExpired(false); // Reset auth state
    setDuplicates([]); // Reset duplicates
    startKeepAlive(); // Keep session alive during uploads
    const uploadedCount = { success: 0, failed: 0, processing: 0 };

    // Check for duplicates before uploading
    try {
      const checkRes = await fetch('/api/images/check-duplicates', {
        method: 'POST',
        credentials: 'include',
        headers: withAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          files: files.map((f, i) => ({ filename: f.name, size: f.size, index: i }))
        })
      });
      if (checkRes.ok) {
        const { duplicates: dupes } = await checkRes.json();
        if (dupes?.length > 0) {
          setDuplicates(dupes);
          showToast(`${dupes.length} file(s) already exist in your library`, '', 'warning');
        }
      }
    } catch (e) {
      // Fail open - don't block uploads if check fails
      console.warn('Duplicate check failed:', e);
    }

    // Upload in parallel with concurrency limit
    // Sort by file size (largest first) to front-load heavy uploads
    // Keep original indices for UI consistency
    const uploadQueue = buildUploadQueue(files, progress).filter(item => typeof onlyIndex !== 'number' || item.index === onlyIndex);
    const inProgress = [];

    const uploadFile = async ({ file, index }) => {
      let lastError = null;

      for (let attempt = 1; attempt <= MAX_RETRY_ATTEMPTS; attempt++) {
        if (!sessionCurrent() || uploadPause.current.paused) return;
        try {
          const statusPrefix = attempt > 1 ? `retry ${attempt}/${MAX_RETRY_ATTEMPTS}` : 'uploading';
          setProgress(prev => ({ ...prev, [index]: { status: statusPrefix, percent: 0 } }));
          setInitializing(false); // Clear initializing once first file starts

          await runUploadTransport({
            presignedEnabled: USE_PRESIGNED_UPLOAD,
            presigned: () => uploadFilePresigned(file, index, statusPrefix, uploadedCount),
            traditional: () => uploadFileTraditional(file, index, statusPrefix, uploadedCount)
          });
          return;

        } catch (error) {
          if (!sessionCurrent() || error.name === 'AbortError') return;
          lastError = error;
          if (isUploadPaused(error)) {
            uploadPause.current.stop(error);
            showToast(`Uploads paused. Retry after ${new Date(error.retryAt).toLocaleTimeString()}.`, 'info');
            break;
          }
          console.warn(`Upload attempt ${attempt}/${MAX_RETRY_ATTEMPTS} failed for ${file.name}:`, error.message);

          if ([400, 403, 413, 415].includes(error.status)) break;
          if (attempt < MAX_RETRY_ATTEMPTS) {
            // Exponential backoff: 1s, 2s, 4s
            const delay = RETRY_BASE_DELAY * Math.pow(2, attempt - 1);
            setProgress(prev => ({ ...prev, [index]: { status: `waiting to retry (${Math.round(delay/1000)}s)`, percent: 0 } }));
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
      }

      // All retries exhausted
      setProgress(prev => ({ ...prev, [index]: uploadFailureEntry(lastError) }));
      uploadedCount.failed++;
      console.error(`Failed to upload ${file.name} after ${MAX_RETRY_ATTEMPTS} attempts:`, lastError);
    };
    
    // Process uploads with concurrency limit based on selected mode
    const maxConcurrent = UPLOAD_MODES[uploadMode]?.concurrent || UPLOAD_MODES[DEFAULT_UPLOAD_MODE].concurrent;
    while (sessionCurrent() && (uploadQueue.length > 0 || inProgress.length > 0)) {
      // Start new uploads up to the concurrency limit
      while (!uploadPause.current.paused && uploadQueue.length > 0 && inProgress.length < maxConcurrent) {
        const item = uploadQueue.shift();
        const uploadPromise = uploadFile(item).finally(() => {
          const idx = inProgress.indexOf(uploadPromise);
          if (idx !== -1) inProgress.splice(idx, 1);
        });
        inProgress.push(uploadPromise);
      }
      
      if (uploadPause.current.paused && inProgress.length === 0) break;
      // Wait for at least one upload to complete
      if (inProgress.length > 0) {
        await Promise.race(inProgress);
      }
    }
    
    if (!sessionCurrent()) return;
    // Notify the parent to refresh its gallery; retain actionable per-file results.
    if (onUploadComplete) {
      onUploadComplete({
        albumId: selectedAlbumId,
        success: uploadedCount.success,
        failed: uploadedCount.failed,
        processing: uploadedCount.processing,
        autoClose: false, // Results remain visible until the customer dismisses them.
        hasProcessing: uploadedCount.processing > 0
      });
    }

    // Clean up local state
    setUploading(false);
    setInitializing(false);
    setAutoClear(false);
    stopKeepAlive(); // Stop keep-alive pings
  }

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
  };

  const formatEtaMs = (ms) => {
    if (ms === null || ms === undefined) return null;
    const clamped = Math.max(0, Number(ms) || 0);
    if (!clamped) return 'moments';
    const mins = Math.floor(clamped / 60000);
    const secs = Math.round((clamped % 60000) / 1000);
    if (mins > 0) return `${mins}m ${secs}s`;
    return `${secs}s`;
  };

  // Aggregate progress summary
  const totalFiles = files.length;
  const progressEntries = Object.entries(progress);
  const progressList = progressEntries.map(([, v]) => v);

  // --- FIXED COUNTING LOGIC START ---

  // Helper: Check if a file has successfully finished the "Upload to S3" phase
  // It is considered uploaded if it is confirming, processing, complete,
  // or if it failed BUT has an imageId (meaning the server knows about it)
  const isUploadedToS3 = (p) => {
    if (!p) return false;
    const status = p.status;
    if (status === 'confirming' || status === 'processing' || status === 'complete') return true;
    if (status === 'error' && (p.imageId || p.needsProcessing)) return true;
    return false;
  };

  // 1. Upload Bar Metrics (Decoupled from Processing status)
  const uploadedToS3Raw = progressList.filter(isUploadedToS3).length;
  // Ensure uploaded never exceeds total (safety after removals)
  const uploadedToS3Count = Math.min(uploadedToS3Raw, totalFiles);

  // Calculate overall upload percentage
  const overallPercent = totalFiles > 0 ? Math.round((uploadedToS3Count / totalFiles) * 100) : 0;

  // 2. Processing Bar Metrics (Decoupled from Upload progress)
  // Items that have needsProcessing flag set (already uploaded RAW files)
  const processingItems = progressList.filter(p => p?.needsProcessing);
  const processingDoneRaw = processingItems.filter((p) => p?.status === 'complete' || isTerminalEntryFailure(p)).length;

  // Count RAW files still in the queue (not yet uploaded)
  const totalRawFiles = files.filter(f => f && isRawExt(f.name)).length;

  // Processing total: max of items in progress and RAW files in queue
  // After removals, processingItems accurately reflects what's being tracked
  const processingTotal = Math.max(processingItems.length, totalRawFiles);

  // Ensure done never exceeds total (safety after removals)
  const processingDone = Math.min(processingDoneRaw, processingTotal);

  // Completion check
  const startedCount = progressEntries.length;
  const failedUploadCount = progressList.filter((p) => p?.status === 'error' && !isUploadedToS3(p)).length;
  const failedCount = failedUploadCount + processingItems.filter(isTerminalEntryFailure).length;
  // All uploads done if (Uploaded + Failed_Uploads) >= Total
  const allUploadsToS3Done = uploadedToS3Count + progressList.filter(p => p?.status === 'error' && !isUploadedToS3(p)).length >= totalFiles && startedCount >= totalFiles;

  // --- END FIXED LOGIC ---

  // UI Helper Counts (for labels)
  const completedCount = progressList.filter(p => p?.status === 'complete').length;
  const processingCount = progressList.filter((p) => p?.status === 'processing' || isRecoverableEntryFailure(p)).length;
  const confirmingCount = progressList.filter(p => p?.status === 'confirming').length;
  const waitingCount = progressList.filter(p => {
    const status = String(p?.status || '').toLowerCase();
    return status.startsWith('waiting for server') || status.startsWith('queued');
  }).length;
  // failedCount is already defined above
  const failedAfterUploadCount = processingItems.filter(isTerminalEntryFailure).length;
  const processingFailed = processingItems.filter(isTerminalEntryFailure).length;
  const processingQueued = processingItems.filter((p) => (
    isProcessingLikeEntry(p) && ['queued', 'pending'].includes(getProcessingStatus(toProcessingState(p)))
  )).length;
  const processingActive = processingItems.filter((p) => (
    isProcessingLikeEntry(p) && !['queued', 'pending'].includes(getProcessingStatus(toProcessingState(p)))
  )).length;
  const sessionQueue = processingQueue?.session || null;
  const sessionTotal = Number(sessionQueue?.requested || 0);
  const sessionReady = Number(sessionQueue?.ready || 0);
  const sessionFailed = Number(sessionQueue?.failed || 0);
  const sessionPending = Number(sessionQueue?.pending || 0);
  const sessionQueued = Number(sessionQueue?.queued || 0);
  const sessionProcessing = Number(sessionQueue?.processing || 0);
  const sessionInProgress = Number(sessionQueue?.in_progress || 0);
  const computeDisplayTotalBase = Math.max(processingTotal, sessionTotal);
  const computeDisplayReady = Math.max(processingDone, sessionReady);
  const computeDisplayFailed = Math.max(processingFailed, sessionFailed);
  const computeDisplayQueued = Math.max(processingQueued, sessionPending + sessionQueued);
  const computeDisplayProcessing = Math.max(processingActive, sessionProcessing);
  const computeDisplayInProgress = Math.max(computeDisplayQueued + computeDisplayProcessing, sessionInProgress);
  const computeDisplayTotal = Math.max(computeDisplayTotalBase, computeDisplayReady + computeDisplayFailed);
  const computeExpected = computeDisplayTotalBase > 0;
  const computeStarted = allUploadsToS3Done && (computeDisplayInProgress > 0 || computeDisplayReady > 0 || computeDisplayFailed > 0);
  const hasBackgroundProcessing = !uploading && allUploadsToS3Done && (processingCount > 0 || computeDisplayInProgress > 0);
  const computePanelActive = hasBackgroundProcessing;
  const isFlowLocked = uploading || initializing || hasBackgroundProcessing;
  const canMinimize = isFlowLocked;
  const canCloseUploader = Boolean(onRequestClose) && !uploading && !initializing;
  const uploadButtonDisabled = totalFiles === 0 || isFlowLocked || buildUploadQueue(files, progress).length === 0;
  const uploadButtonLabel = uploading
    ? (allUploadsToS3Done ? 'Processing...' : (initializing ? 'Initializing...' : 'Uploading...'))
    : (hasBackgroundProcessing ? 'Working in background...' : 'Upload All');

  let flowBannerTone = 'idle';
  let flowBannerTitle = 'Ready to upload';
  let flowBannerMessage = 'Choose an album if you want, then upload your photos.';
  if (initializing) {
    flowBannerTone = 'working';
    flowBannerTitle = 'Preparing upload';
    flowBannerMessage = 'Getting your photos ready to transfer.';
  } else if (uploading && !allUploadsToS3Done) {
    flowBannerTone = 'working';
    flowBannerTitle = 'Uploads continue in the background';
    flowBannerMessage = 'Keep browsing while your photos upload to Dailey Cloud Storage.';
  } else if (hasBackgroundProcessing) {
    flowBannerTone = 'processing';
    flowBannerTitle = 'Uploads complete. Your photos are being prepared';
    flowBannerMessage = 'Placeholders and previews will appear in your library automatically. You can close this uploader and keep browsing.';
  } else if (allUploadsToS3Done && totalFiles > 0 && failedCount > 0) {
    // Never say "all complete" when files failed, that hid a dead upload path
    // ("All uploads complete" sitting right next to "Needs attention").
    flowBannerTone = 'error';
    flowBannerTitle = `${Math.max(totalFiles - failedCount, 0)} of ${totalFiles} uploaded, ${failedCount} need${failedCount === 1 ? 's' : ''} attention`;
    flowBannerMessage = 'Some files did not upload. Check the list below, then retry or remove them.';
  } else if (allUploadsToS3Done && totalFiles > 0) {
    flowBannerTone = 'complete';
    flowBannerTitle = 'All uploads complete';
    flowBannerMessage = 'Everything is ready. You can add more photos or close the uploader.';
  }

  // File stats (size, type counts)
  const stats = useMemo(() => {
    const byExt = new Map();
    let totalBytes = 0;
    let maxBytes = 0;
    files.forEach(f => {
      if (!f) return;
      totalBytes += f.size || 0;
      maxBytes = Math.max(maxBytes, f.size || 0);
      const ext = (f.name || '').toLowerCase().split('.').pop() || 'file';
      byExt.set(ext, (byExt.get(ext) || 0) + 1);
    });
    const topTypes = Array.from(byExt.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4);
    const totalSizeFormatted = formatFileSize(totalBytes);
    const largestFormatted = formatFileSize(maxBytes);
    return { byExt, topTypes, totalBytes, totalSizeFormatted, largestFormatted };
  }, [files]);

  const oversizeSummary = useMemo(() => {
    if (!oversizedFiles.length) return null;
    const names = oversizedFiles.slice(0, 3).map(file => file.name);
    const suffix = oversizedFiles.length > 3 ? ` +${oversizedFiles.length - 3} more` : '';
    return `${oversizedFiles.length} skipped (${formatFileSize(MAX_FILE_BYTES)} max): ${names.join(', ')}${suffix}`;
  }, [oversizedFiles]);

  const processingActiveIds = useMemo(() => {
    const ids = Object.values(progress)
      .filter(entry => entry?.status === 'processing' && entry?.imageId)
      .map(entry => entry.imageId);
    return ids.sort();
  }, [progress]);

  const processingSessionIds = useMemo(() => {
    const ids = Object.values(progress)
      .filter(entry => entry?.imageId && entry?.needsProcessing)
      .map(entry => entry.imageId);
    return ids.sort();
  }, [progress]);

  // Keep refs so the poll closure always reads the latest lists
  const processingActiveIdsRef = useRef(processingActiveIds);
  const processingSessionIdsRef = useRef(processingSessionIds);
  processingActiveIdsRef.current = processingActiveIds;
  processingSessionIdsRef.current = processingSessionIds;
  const hasProcessing = processingActiveIds.length > 0;

  useEffect(() => {
    if (!hasProcessing) return () => {};
    let cancelled = false;
    let inFlight = false;

    const poll = async () => {
      const activeIds = processingActiveIdsRef.current;
      const sessionIds = processingSessionIdsRef.current;
      if (!activeIds.length || !sessionIds.length || inFlight) return;
      inFlight = true;
      try {
        // Use main API (empty baseUrl), not upload proxy, for status polling
        const res = await uploadFetch(vaultCollectionId ? `/api/vault/collections/${encodeURIComponent(vaultCollectionId)}/processing-status` : '/api/images/processing/status', vaultCollectionId ? { method: 'GET', credentials: 'include' } : {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ image_ids: sessionIds })
        }, '');
        if (!res.ok) return;
        const data = await res.json();
        const statuses = vaultCollectionId
          ? Object.fromEntries((data.files || []).map(file => [file.id, { status: file.status === 'archived' ? 'ready' : file.status === 'processing_proxy' ? 'processing' : file.status }]))
          : data.images || {};
        if (data.queue || data.session) {
          setProcessingQueue({
            ...(data.queue || {}),
            session: data.session || null
          });
        }
        if (cancelled || !sessionCurrent()) return;
        setProgress(prev => {
          const next = { ...prev };
          Object.entries(prev).forEach(([key, entry]) => {
            if (!entry || entry.status !== 'processing' || !entry.imageId) return;
            const status = statuses[entry.imageId];
            if (!status) return;
            const nextStatusRaw = String(status.status || entry.processingStatus || '').toLowerCase();
            const normalizedError = status.error_detail || status.error || entry.processingError || null;
            const retryableFailure = nextStatusRaw === 'failed' && isRecoverableProcessingFailure({
              processing_status: nextStatusRaw,
              processing_error: normalizedError
            });
            let nextState = entry.status;
            if (nextStatusRaw === 'ready') nextState = 'complete';
            if (nextStatusRaw === 'failed') nextState = retryableFailure ? 'processing' : 'error';
            if (nextStatusRaw === 'not_found') nextState = 'error';
            const notFoundError = nextStatusRaw === 'not_found'
              ? { category: 'system_error', retryable: true, message: 'Image record not found during processing poll' }
              : null;
            next[key] = {
              ...entry,
              status: nextState,
              processingStatus: retryableFailure ? 'recovering' : (nextStatusRaw || entry.processingStatus),
              processingMode: status.processing_mode || entry.processingMode || null,
              processingEtaMs: status.estimated_ms ?? entry.processingEtaMs ?? null,
              processingError: notFoundError || normalizedError,
              processingErrorMessage: status.error || notFoundError?.message || entry.processingErrorMessage || null,
              processingErrorCategory: status.error_category || notFoundError?.category || entry.processingErrorCategory || null,
              processingRetryCount: status.retry_count ?? entry.processingRetryCount ?? 0
            };
          });
          return next;
        });

      } catch {
        // ignore polling errors
      } finally {
        inFlight = false;
      }
    };

    poll();
    const t = setInterval(poll, 1250);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [hasProcessing, uploadFetch, vaultCollectionId]);

  // Notify from committed state, never from inside a deferred/replayed updater.
  const completionCallbackRef = useRef(onUploadComplete);
  completionCallbackRef.current = onUploadComplete;
  const notifiedProcessingRef = useRef(new Set());
  useEffect(() => {
    const ready = Object.entries(progress).filter(([, entry]) =>
      entry?.needsProcessing && entry.status === 'complete' && entry.imageId
      && !notifiedProcessingRef.current.has(entry.imageId));
    if (!ready.length || !sessionCurrent()) return;
    ready.forEach(([, entry]) => notifiedProcessingRef.current.add(entry.imageId));
    setPreviews(previous => {
      const next = new Map(previous);
      ready.forEach(([index, entry]) => {
        if (!entry.filename || vaultCollectionId) return;
        const old = next.get(Number(index));
        if (old && !String(old).startsWith('blob:')) return;
        // The committed-preview effect releases displaced owned blob URLs.
        next.set(Number(index), `/media/variants/sm/${String(entry.filename).replace(/\.[^.]+$/, '.jpg')}`);
      });
      return next;
    });
    completionCallbackRef.current?.({ albumId: selectedAlbumId, processingComplete: ready.length, refresh: true });
  }, [progress]);

  useEffect(() => {
    if (!autoClear || uploading) return;
    if (processingActiveIds.length > 0) return;
    if (!files.length) {
      setAutoClear(false);
      return;
    }
    const timer = setTimeout(() => {
      cleanupPreviews();
      setFiles([]);
      setProgress({});
      setPreviews(new Map());
      setAutoClear(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, [autoClear, uploading, processingActiveIds.join(','), files.length]);

  // Track items that complete and animate them out
  const prevProgressRef = useRef({});
  useEffect(() => {
    const prevProgress = prevProgressRef.current;
    const newExiting = new Set();

    files.forEach((_, idx) => {
      const prev = prevProgress[idx];
      const curr = progress[idx];
      const prevStatus = prev?.status || '';
      const currStatus = curr?.status || '';

      // Item just became complete or processing (transitioning out of active)
      const wasActive = !prevStatus || prevStatus === 'error' || prevStatus === 'uploading' ||
                        prevStatus.startsWith('retry') || prevStatus.startsWith('waiting') || prevStatus === 'confirming';
      const isNowComplete = currStatus === 'complete' || currStatus === 'processing';

      if (wasActive && isNowComplete) {
        newExiting.add(idx);
      }
    });

    if (newExiting.size > 0) {
      setExitingItems(prev => new Set([...prev, ...newExiting]));
      // Remove from exiting set after animation completes
      setTimeout(() => {
        setExitingItems(prev => {
          const next = new Set(prev);
          newExiting.forEach(idx => next.delete(idx));
          return next;
        });
      }, 350); // Slightly longer than CSS transition
    }

    prevProgressRef.current = { ...progress };
  }, [progress, files.length]);

  // ETA estimation based on elapsed time and overall percent
  const eta = useMemo(() => {
    if (!uploading || overallPercent <= 0 || !uploadStartRef.current) return null;
    const elapsedMs = Date.now() - uploadStartRef.current;
    const fraction = overallPercent / 100;
    if (fraction <= 0) return null;
    const totalMs = elapsedMs / fraction;
    const remainingMs = Math.max(0, totalMs - elapsedMs);
    const mins = Math.floor(remainingMs / 60000);
    const secs = Math.round((remainingMs % 60000) / 1000);
    return `${mins}m ${secs}s`;
  }, [uploading, overallPercent]);

  const buildProcessingFailedDisplay = (entry) => {
    const normalizedError = normalizeProcessingError(entry?.processingError);
    const errorCategory = entry?.processingErrorCategory || categorizeError(normalizedError || entry?.processingError);
    const errorInfo = ERROR_CATEGORIES[errorCategory];
    const backendMessage = String(
      entry?.processingErrorMessage
      || normalizedError?.message
      || entry?.processingError
      || ''
    ).trim();
    const retryable = (normalizedError?.retryable ?? errorInfo?.retryable ?? true) === true;
    return {
      label: 'Needs attention',
      tone: 'error',
      showBar: false,
      canRemove: true,
      canRetry: retryable,
      canReport: true,
      imageId: entry?.imageId,
      errorMessage: backendMessage || errorInfo?.message,
      errorAction: errorInfo?.action,
      rawError: backendMessage || entry?.processingError,
      filename: entry?.filename
    };
  };

  const buildRecoveringDisplay = (entry) => ({
    label: 'Processing',
    tone: 'processing',
    showBar: false,
    canRemove: !uploading,
    canRetry: false,
    imageId: entry?.imageId,
    sub: 'Photo Harbor is recovering this preview automatically.'
  });

  const getStatusDisplay = (entry) => {
    if (!entry) {
      return {
        label: uploading ? 'Preparing upload' : 'Ready to upload',
        tone: 'pending',
        showBar: false,
        canRemove: !uploading,
        canRetry: false
      };
    }
    const rawStatus = String(entry.status || '').toLowerCase();
    if (rawStatus.startsWith('retry')) {
      return { label: 'Retrying upload', tone: 'pending', showBar: false, canRemove: false, canRetry: false };
    }
    if (rawStatus.startsWith('waiting to retry')) {
      return { label: 'Retrying soon', tone: 'pending', showBar: false, canRemove: false, canRetry: false };
    }
    if (rawStatus.startsWith('waiting')) {
      return { label: 'Preparing upload', tone: 'pending', showBar: false, canRemove: false, canRetry: false };
    }
    if (rawStatus === 'uploading') {
      return { label: `Uploading ${entry.percent || 0}%`, tone: 'uploading', showBar: true, canRemove: false, canRetry: false };
    }
    if (rawStatus === 'confirming') {
      return { label: 'Confirming...', tone: 'uploading', showBar: false, canRemove: false, canRetry: false };
    }
    if (rawStatus === 'processing') {
      const processingStage = String(entry.processingStatus || 'queued').toLowerCase();
      // Check if processing actually failed on the server side
      if (processingStage === 'recovering' || (processingStage === 'failed' && isRecoverableEntryFailure(entry))) {
        return buildRecoveringDisplay(entry);
      }
      if (processingStage === 'failed') {
        return buildProcessingFailedDisplay(entry);
      }
      // Allow removing stuck processing items (after upload completes)
      return {
        label: 'Processing',
        tone: 'processing',
        showBar: false,
        sub: 'Your preview will appear automatically.',
        canRemove: !uploading,
        canRetry: false,
        imageId: entry.imageId
      };
    }
    if (rawStatus === 'complete') {
      return { label: 'Ready', tone: 'success', showBar: false, canRemove: false, canRetry: false };
    }
    if (rawStatus === 'error') {
      if (isRecoverableEntryFailure(entry)) {
        return buildRecoveringDisplay(entry);
      }
      const processingStage = String(entry.processingStatus || '').toLowerCase();
      if (processingStage === 'failed' || entry.processingError) {
        return buildProcessingFailedDisplay(entry);
      }
      return { label: 'Needs attention', tone: 'error', showBar: false, canRemove: true, canRetry: false };
    }
    return { label: entry.status || 'Pending', tone: 'pending', showBar: false, canRemove: false, canRetry: false };
  };

  // Reorder helpers (disabled while upload/compute is active)
  function onTileDragStart(e, index) {
    if (isFlowLocked) return;
    setDragIndex(index);
    setDragOverIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  }
  function onTileDragOver(e, index) {
    if (isFlowLocked) return;
    e.preventDefault();
    if (index !== dragOverIndex) setDragOverIndex(index);
  }
  function onTileDrop(e, index) {
    if (isFlowLocked) return;
    e.preventDefault();
    e.stopPropagation();
    if (dragIndex === null || index === dragIndex) {
      setDragIndex(null);
      setDragOverIndex(null);
      return;
    }
    // Reorder files, previews, and progress mapping consistently
    const reorder = (arr) => {
      const next = arr.slice();
      const [moved] = next.splice(dragIndex, 1);
      next.splice(index, 0, moved);
      return next;
    };
    setFiles(prev => reorder(prev));
    // Rebuild previews map with new order
    setPreviews(prev => {
      const newMap = new Map();
      const reorderedFiles = reorder(files);
      reorderedFiles.forEach((file, newIndex) => {
        const oldIndex = files.indexOf(file);
        if (oldIndex !== -1 && prev.get(oldIndex)) {
          newMap.set(newIndex, prev.get(oldIndex));
        }
      });
      return newMap;
    });
    setProgress(prev => {
      const progArray = files.map((_, i) => prev[i] || null);
      const reProg = reorder(progArray);
      const mapped = {};
      reProg.forEach((p, i) => { if (p) mapped[i] = p; });
      return mapped;
    });
    setDragIndex(null);
    setDragOverIndex(null);
  }
  function onTileDragEnd() {
    setDragIndex(null);
    setDragOverIndex(null);
  }

  const panelFileIds = useRef(new WeakMap());
  const nextPanelId = useRef(0);
  const panelItems = files.map((file,index)=>{
    if(!panelFileIds.current.has(file))panelFileIds.current.set(file,'upload-'+ ++nextPanelId.current);
    const occurrence=files.slice(0,index).filter(other=>other===file).length;
    const entry=progress[index];const status=entry?.status||'';const display=getStatusDisplay(entry);
    const phase=status==='complete'?'ready':status==='error'?'failed':status==='processing'?'processing':status==='confirming'?'confirming':status.startsWith('retry')||status.startsWith('waiting to retry')?'retrying':status?'transferring':'queued';
    return {id:panelFileIds.current.get(file)+'-'+occurrence,name:file.name,bytes:file.size,previewURL:previews.get(index),phase,phaseLabel:display.label,transferPercent:entry?.percent,error:display.errorMessage,statusDetail:display.sub,canRetry:!isFlowLocked&&status==='error'&&(entry?.imageId?display.canRetry:!entry?.needsProcessing),canRemove:!isFlowLocked&&!entry?.imageId,canInspect:Boolean(display.canReport)};
  });
  const panelCounts={queued:0,transferring:0,processing:0,ready:0,failed:0};
  for(const item of panelItems){const key=['confirming','retrying'].includes(item.phase)?'transferring':item.phase;panelCounts[key]=(panelCounts[key]||0)+1;}

  const reportDialogRef = useUploadDialog(Boolean(bugReportData), () => setBugReportData(null));

  return (
    <>
      {showPuziakBlock && (
        <PuziakBlock
          blockedFiles={blockedFiles}
          onClose={() => {
            setShowPuziakBlock(false);
            setBlockedFiles([]);
          }}
        />
      )}

      <div className="batch-uploader">
      {/* Hidden file inputs - always present */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={UPLOAD_FILE_ACCEPT}
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
      <input
        ref={captureInputRef}
        type="file"
        multiple
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
      {/* Folder picker. webkitdirectory/directory are set imperatively because
          React does not pass them reliably as JSX props; this makes the OS file
          dialog select an entire folder (all photos inside, subfolders too). */}
      <input
        ref={(el) => {
          folderInputRef.current = el;
          if (el) {
            el.setAttribute('webkitdirectory', '');
            el.setAttribute('directory', '');
            el.setAttribute('mozdirectory', '');
          }
        }}
        type="file"
        multiple
        data-testid="folder-input"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      <UploadPanelView
        title={archiveFolderId ? 'Upload scans' : vaultCollectionId ? 'Upload to Vault' : 'Upload Photos'}
        subtitle="Your originals stay intact" items={panelItems}
        summary={{...panelCounts,totalBytes:stats.totalBytes,transferPercent:stats.totalBytes > 0 ? files.reduce((sum,file,index)=>sum+(file.size||0)*Math.max(0,Math.min(100,progress[index]?.percent||0)),0)/stats.totalBytes : 0,title:flowBannerTitle,detail:flowBannerMessage,tone:flowBannerTone==='complete'?'success':flowBannerTone==='error'?'error':hasBackgroundProcessing?'processing':'neutral'}}
        destination={<strong>{archiveFolderId?'Current Scan Archive folder':vaultCollectionId?'Current Vault collection':libraryScope==='client_upload'?'Current client job':selectedAlbumId?'Selected album':'Photo library'}</strong>}
        settings={<><label className="upload-speed-field">Upload speed<select aria-label="Upload speed" value={uploadMode} disabled={isFlowLocked} onChange={e=>handleUploadModeChange(e.target.value)}>{Object.entries(UPLOAD_MODES).map(([key,mode])=><option key={key} value={key}>{mode.label} — {mode.description}</option>)}</select></label>{!vaultCollectionId&&<AlbumSelectorDropdown selectedAlbumId={selectedAlbumId} onAlbumChange={setSelectedAlbumId} hideFromLibrary={hideFromLibrary} onHideChange={setHideFromLibrary} disabled={isFlowLocked}/>}</>}
        intakeHint={'RAW, HEIC and standard images · Max per file: '+formatFileSize(MAX_FILE_BYTES)}
        intakeControls={<>{canCapture&&<button type="button" disabled={isFlowLocked} onClick={()=>captureInputRef.current?.click()}>Camera</button>}<details><summary>Supported formats</summary>{SUPPORTED_EXTENSIONS_TEXT}</details></>}
        notice={<>{oversizeSummary&&<div role="alert" className="upload-warning">{oversizeSummary}</div>}          {authExpired && (
            <div className="upload-auth-expired">
              <strong>Session expired</strong>: Please{' '}
              <a href="/login" onClick={(e) => { e.preventDefault(); window.location.reload(); }}>
                refresh the page
              </a>{' '}
              to log in again, then retry your uploads.
            </div>
          )}
</>}
        intakeDisabled={isFlowLocked} dragActive={dragActive} onSelectFiles={()=>fileInputRef.current?.click()} onSelectFolder={()=>folderInputRef.current?.click()} onDrop={handleDrop} onDragOver={handleDrag} onDragLeave={handleDrag}
        onUpload={files.length?()=>uploadFiles():undefined} uploadDisabled={uploadButtonDisabled} uploadLabel={uploadButtonLabel}
        onClear={clearFiles} canClear={!isFlowLocked&&files.length>0}
        onClose={canCloseUploader?onRequestClose:undefined} closeLabel={hasBackgroundProcessing?'Keep browsing':'Close uploader'} onMinimize={canMinimize?handleMinimize:undefined}
        onRetry={id=>{const index=panelItems.findIndex(item=>item.id===id);if(index<0)return;const entry=progress[index];if(entry?.imageId)retryProcessing(entry.imageId);else uploadFiles(index);}}
        onRemove={id=>{const index=panelItems.findIndex(item=>item.id===id);if(index>=0)removeFile(index);}}
        onInspect={id=>{const index=panelItems.findIndex(item=>item.id===id);const entry=progress[index];if(index>=0)setBugReportData({filename:files[index].name,imageId:entry?.imageId,error:entry?.processingErrorMessage||entry?.processingError?.message||'',errorCategory:categorizeError(entry?.processingError)});}}
        detailControls={files.length?<>          {totalFiles > 0 ? (
            <details className="upload-transfer-details"><summary>Transfer &amp; processing details</summary>
            <div className="upload-summary" role="status" aria-live="polite">
              <div className="summary-header">
                <div>
                  <div className="summary-title">Upload progress</div>
                  <div className="summary-primary">
                    {uploading && !allUploadsToS3Done ? (
                      <span>
                        Uploading {uploadedToS3Count}/{totalFiles}
                        {failedCount > 0 ? ` • ${failedCount} need attention` : ''}
                        {eta ? ` • ETA ~ ${eta}` : ''}
                      </span>
                    ) : hasBackgroundProcessing ? (
                      <span>
                        Uploads complete • Your library will update automatically
                        {computeDisplayFailed > 0 ? ` • ${computeDisplayFailed} need attention` : ''}
                      </span>
                    ) : (
                      <span>
                        {uploadedToS3Count > 0 ? `${uploadedToS3Count}/${totalFiles} uploaded` : `${totalFiles} selected`}
                        {failedCount > 0 ? ` • ${failedCount} need attention` : ''}
                      </span>
                    )}
                  </div>
                </div>
                <div className="upload-status-tabs">
                  <button
                    type="button"
                    className={`upload-status-tab ${activeTab === 'active' ? 'active' : ''}`}
                    onClick={() => setActiveTab('active')}
                  >
                    <span className="tab-label">Active</span>
                    <span className="tab-count">({totalFiles - uploadedToS3Count - failedCount})</span>
                  </button>
                  <button
                    type="button"
                    className={`upload-status-tab ${activeTab === 'completed' ? 'active' : ''}`}
                    onClick={() => setActiveTab('completed')}
                  >
                    <span className="tab-label">Completed</span>
                    <span className="tab-count">({uploadedToS3Count})</span>
                  </button>
                  <button
                    type="button"
                    className={`upload-status-tab ${activeTab === 'previews' ? 'active' : ''}`}
                    onClick={() => setActiveTab('previews')}
                  >
                    <span className="tab-label">Previews</span>
                    <span className="tab-count">({totalFiles})</span>
                  </button>
                </div>
              </div>

              <div className="summary-section">
                <div className="summary-section-head">
                  <span className="storage-label">
                    <span className={`cloud-icon${uploading && !allUploadsToS3Done ? ' cloud-icon-animated' : ''}`}>☁️</span>
                    {uploading && !allUploadsToS3Done ? 'Uploading to Dailey Storage' : 'Dailey Cloud Storage'}
                  </span>
                  <span>{uploadedToS3Count}/{totalFiles}</span>
                </div>
                <div className="summary-bar">
                  <div className="summary-fill" style={{ width: `${overallPercent}%` }} />
                </div>
                <div className="summary-meta summary-meta-wrap">
                  <span className="summary-meta-item">Total: {stats.totalSizeFormatted}</span>
                  {stats.topTypes.map(([ext, count]) => (
                    <span key={ext} className="summary-meta-item summary-meta-type">{ext.toUpperCase()} {count}</span>
                  ))}
                  <span className="summary-meta-item">Largest: {stats.largestFormatted}</span>
                </div>
              </div>

              <div className="summary-section">
                <div className="summary-section-head">
                  <span className="burst-label">
                    {computePanelActive ? <span className="processing-spinner" /> : null}
                    {allUploadsToS3Done ? (hasBackgroundProcessing ? 'Processing' : 'Processing complete') : 'After upload'}
                  </span>
                  <span>
                    {!allUploadsToS3Done
                      ? (computeExpected ? 'Starts after upload' : 'Idle')
                      : (hasBackgroundProcessing ? 'In background' : 'Finished')}
                  </span>
                </div>
                <div className="summary-meta">
                  {!allUploadsToS3Done ? (
                    computeExpected ? (
                      <>
                        <span>Photo Harbor will prepare previews and metadata automatically after upload finishes.</span>
                        <span className="summary-meta-subtle">
                          You do not need to wait on this screen for processing to begin.
                        </span>
                      </>
                    ) : (
                      <span className="burst-idle">Idle</span>
                    )
                  ) : hasBackgroundProcessing ? (
                    <>
                      <span>Your photos are safely uploaded. Photo Harbor is preparing previews and metadata in the background.</span>
                      <span className="summary-meta-subtle">
                        Watch the placeholder tiles in your library. They will fill in automatically as each photo becomes ready.
                      </span>
                      <span className="summary-meta-subtle">
                        You can close this uploader and keep using the site.
                      </span>
                      {computeDisplayFailed > 0 ? (
                        <span className="summary-meta-subtle">
                          {computeDisplayFailed} photo{computeDisplayFailed !== 1 ? 's need' : ' needs'} attention. You can retry them from this uploader later.
                        </span>
                      ) : null}
                    </>
                  ) : (
                    <span className="burst-idle">{failedCount > 0 ? 'Finished with errors. Review the file results above.' : 'Previews and metadata are ready.'}</span>
                  )}
                </div>
              </div>

              {uploading && waitingCount > 0 && (
                <div className="upload-queue-note">
                  Photo Harbor is balancing uploads in the background. They will continue automatically.
                </div>
              )}
            </div>
            </details>
          ) : null}
<details><summary>Advanced file controls</summary>          {activeTab === 'active' && (
            <div className="upload-tab-content" key="tab-active">
              {/* Collapsible file list header */}
              <button
                type="button"
                className="file-list-drawer-toggle"
                onClick={() => setFileListCollapsed(!fileListCollapsed)}
              >
                <span className="file-list-drawer-title">
                  {fileListCollapsed ? '▶' : '▼'} Individual Files
                </span>
                <span className="file-list-drawer-count">
                  {files.filter((_, idx) => {
                    const status = progress[idx]?.status || '';
                    return !status || status === 'error' || status === 'uploading' || status.startsWith('retry') || status.startsWith('waiting') || status === 'confirming';
                  }).length} active
                </span>
              </button>

              {!fileListCollapsed && (
                <div className="upload-status-list">
                  {/* Sort files by size (largest first) and map with original indices */}
                  {files
                    .map((file, idx) => ({ file, idx }))
                    .sort((a, b) => (b.file.size || 0) - (a.file.size || 0))
                    .map(({ file, idx }) => {
                      const entry = progress[idx];
                      const status = entry?.status || '';
                      // Active = not yet complete/processing (pending, uploading, error, retrying)
                      const isActive = !status || status === 'error' || status === 'uploading' || status.startsWith('retry') || status.startsWith('waiting') || status === 'confirming';
                      const isExiting = exitingItems.has(idx);
                      // Show if active OR if currently animating out
                      if (!isActive && !isExiting) return null;

                      const display = getStatusDisplay(entry);
                      const isDuplicate = duplicates.some(d => d.index === idx);
                      return (
                        <div className={`upload-status-row ${display.tone === 'error' ? 'has-error' : ''} ${isExiting ? 'exiting' : ''}`} key={`status-${idx}`}>
                          {previews.get(idx) && (
                            <img src={previews.get(idx)} alt="" className="upload-status-thumb" />
                          )}
                          <div className="upload-status-main">
                            <div className="upload-status-name" title={file.name}>{file.name}</div>
                            <div className="upload-status-meta">
                              {formatFileSize(file.size)}
                              {isDuplicate && <span className="upload-duplicate-badge" title="This file already exists in your library">duplicate</span>}
                            </div>
                          </div>
                          <div className={`upload-status-pill ${display.tone}`}>{display.label}</div>
                          {display.sub && (
                            <div className="upload-status-sub">{display.sub}</div>
                          )}
                          {display.showBar && (
                            <div className="upload-status-progress">
                              <div className="upload-status-progress-fill" style={{ width: `${entry?.percent || 0}%` }} />
                            </div>
                          )}
                          {/* Error details for failed processing */}
                          {display.errorMessage && (
                            <div className="upload-error-details">
                              <div className="upload-error-message">
                                <FiAlertTriangle size={14} />
                                <span>{display.errorMessage}</span>
                              </div>
                              {display.errorAction && (
                                <div className="upload-error-action">
                                  <strong>Try:</strong> {display.errorAction}
                                </div>
                              )}
                            </div>
                          )}
                          {(display.canRetry || display.canRemove || display.canReport) && (
                            <div className="upload-status-actions">
                              {display.canRetry && display.imageId && (
                                <button
                                  type="button"
                                  className="upload-action-btn retry"
                                  onClick={() => retryProcessing(display.imageId)}
                                  title="Retry processing"
                                >
                                  Retry
                                </button>
                              )}
                              {display.canReport && (
                                <button
                                  type="button"
                                  className="upload-action-btn report"
                                  onClick={() => setBugReportData({
                                    filename: file.name,
                                    imageId: display.imageId,
                                    error: display.rawError,
                                    errorCategory: categorizeError(display.rawError)
                                  })}
                                  title="Report this issue"
                                >
                                  <FiSend size={12} /> Report
                                </button>
                              )}
                              {display.canRemove && (
                                <button
                                  type="button"
                                  className="upload-action-btn remove"
                                  onClick={() => entry ? removeFailedFile(idx) : removeFile(idx)}
                                  title="Remove from list"
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  {/* Show empty state if no active uploads */}
                  {files.every((_, idx) => {
                    const status = progress[idx]?.status || '';
                    return status === 'complete' || status === 'processing';
                  }) && (
                    <div className="upload-status-empty">
                      {processingCount > 0
                        ? 'Uploads finished. Your library will keep filling in automatically while processing finishes.'
                        : 'All uploads complete. You can keep browsing.'}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'completed' && (
            <div className="upload-status-list upload-tab-content" key="tab-completed">
              {(() => {
                // Calculate completed stats
                const completedItems = files.map((file, idx) => {
                  const entry = progress[idx];
                  const status = entry?.status || '';
                  const isCompleted = status === 'complete' || status === 'processing';
                  return isCompleted ? { file, idx, entry, status } : null;
                }).filter(Boolean);

                const completeCount = completedItems.filter(i => i.status === 'complete').length;
                const processingCount = completedItems.filter(i => i.status === 'processing').length;
                const totalSize = completedItems.reduce((sum, i) => sum + (i.file.size || 0), 0);

                if (completedItems.length === 0) {
                  return (
                    <div className="upload-status-empty">
                      No completed uploads yet.
                    </div>
                  );
                }

                return (
                  <>
                    {/* Summary header */}
                    <div
                      className="upload-completed-summary"
                      onClick={() => setCompletedCollapsed(!completedCollapsed)}
                      style={{
                        padding: '12px 16px',
                        background: 'var(--bg-secondary, #f5f5f5)',
                        borderRadius: '8px',
                        marginBottom: '8px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        transition: 'background 0.15s'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '20px' }}>✓</span>
                        <div>
                          <div style={{ fontWeight: 600 }}>
                            {completeCount} safely uploaded
                          </div>
                          <div style={{ fontSize: '12px', opacity: 0.7 }}>
                            {processingCount > 0
                              ? 'Your library will update automatically while previews and metadata finish processing.'
                              : `${formatFileSize(totalSize)} total`}
                          </div>
                        </div>
                      </div>
                      <span style={{
                        transform: completedCollapsed ? 'rotate(0deg)' : 'rotate(180deg)',
                        transition: 'transform 0.2s',
                        opacity: 0.5
                      }}>▼</span>
                    </div>

                    {/* Expanded list */}
                    {!completedCollapsed && completedItems.map(({ file, idx, entry }) => {
                      const display = getStatusDisplay(entry);
                      return (
                        <div className="upload-status-row" key={`completed-${idx}`} style={{
                          animation: 'fadeIn 0.15s ease-out'
                        }}>
                          <div className="upload-status-main">
                            <div className="upload-status-name" title={file.name}>{file.name}</div>
                            <div className="upload-status-meta">
                              {formatFileSize(file.size)}
                            </div>
                          </div>
                          <div className={`upload-status-pill ${display.tone}`}>{display.label}</div>
                          {display.sub && (
                            <div className="upload-status-sub">{display.sub}</div>
                          )}
                        </div>
                      );
                    })}
                  </>
                );
              })()}
            </div>
          )}

          {activeTab === 'previews' && (
            <div className="upload-tab-content" key="tab-previews">
              {/* Show load more button if we have many files */}
              {files.length > MAX_VISIBLE_THUMBNAILS && !showAllThumbnails && (
                <div className="load-more-thumbnails" style={{ padding: '20px', textAlign: 'center', borderBottom: '1px solid #eee' }}>
                  <button 
                    className="button-secondary"
                    onClick={() => setShowAllThumbnails(true)}
                    style={{ marginBottom: '10px' }}
                  >
                    Show all {files.length} files
                  </button>
                  <p className="muted">Showing first {MAX_VISIBLE_THUMBNAILS} files to improve performance</p>
                </div>
              )}

              <div className="upload-preview-actions">
                <button
                  type="button"
                  className="button-minimal"
                  onClick={() => setExpanded(e => !e)}
                  title={expanded ? 'Collapse list' : 'Expand list'}
                >
                  {expanded ? 'Collapse' : 'Expand'}
                </button>
              </div>

              <div className="upload-grid" style={expanded ? { maxHeight: '70vh', overflowY: 'auto' } : undefined}>
                {files.slice(0, showAllThumbnails ? undefined : MAX_VISIBLE_THUMBNAILS).map((file, idx) => {
                  const src = previews.get(idx);
                  const progressData = progress[idx];
                  return (
                    <div
                      className={`upload-tile ${dragOverIndex === idx ? 'drag-over' : ''}`}
                      key={`tile-${idx}`}
                      draggable={!isFlowLocked}
                      onDragStart={(e) => onTileDragStart(e, idx)}
                      onDragOver={(e) => onTileDragOver(e, idx)}
                      onDrop={(e) => onTileDrop(e, idx)}
                      onDragEnd={onTileDragEnd}
                    >
                      {src ? (
                        <img src={src} alt={file.name} />
                      ) : (
                        <div className="upload-tile-fallback">
                          {file.name.split('.').pop()?.toUpperCase() || 'FILE'}
                        </div>
                      )}
                      {!isFlowLocked && !progressData && (
                        <button className="tile-remove" onClick={() => removeFile(idx)} aria-label="Remove from upload">✕</button>
                      )}
                      {progressData?.status === 'uploading' && (
                        <div className="tile-progress">
                          <div className="tile-progress-fill" style={{ width: `${progressData.percent}%` }} />
                        </div>
                      )}
                      {progressData?.status === 'processing' && (
                        <div className="processing-overlay">
                          <span className="processing-spinner" />
                          <span className="processing-label">Processing</span>
                        </div>
                      )}
                      {progressData?.status === 'complete' && (
                        <div className="tile-badge complete" title="Uploaded">✓</div>
                      )}
                      {progressData?.status === 'error' && (
                        <div className="tile-badge error" title="Needs attention">✗</div>
                      )}
                      <div className="tile-label" title={file.name}>
                        <span className="tile-name">{file.name}</span>
                        <span className="tile-size">{formatFileSize(file.size)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
</details></>:null}
      />

      {/* Bug Report Modal */}
      {bugReportData && (
        <div className="bug-report-overlay" onClick={() => setBugReportData(null)}>
          <div ref={reportDialogRef} role="dialog" aria-modal="true" aria-label="Report Processing Issue" tabIndex={-1} className="bug-report-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bug-report-header">
              <h3>Report Processing Issue</h3>
              <button className="bug-report-close" onClick={() => setBugReportData(null)}>×</button>
            </div>
            <div className="bug-report-body">
              <p>Help us improve by reporting this issue. We'll investigate and work on a fix.</p>

              <div className="bug-report-info">
                <div className="bug-report-field">
                  <label>File</label>
                  <span>{bugReportData.filename}</span>
                </div>
                <div className="bug-report-field">
                  <label>Error Type</label>
                  <span>{ERROR_CATEGORIES[bugReportData.errorCategory]?.title || 'Unknown'}</span>
                </div>
                {bugReportData.error && (
                  <div className="bug-report-field">
                    <label>Technical Details</label>
                    <code className="bug-report-code">{bugReportData.error}</code>
                  </div>
                )}
              </div>

              <div className="bug-report-field">
                <label htmlFor="additional-info">Additional Information (optional)</label>
                <textarea
                  id="additional-info"
                  placeholder="Any additional context that might help us fix this issue..."
                  rows={3}
                  onChange={(e) => setBugReportData(prev => ({ ...prev, additionalInfo: e.target.value }))}
                />
              </div>
            </div>
            <div className="bug-report-footer">
              <button
                type="button"
                className="button-secondary"
                onClick={() => setBugReportData(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="button-primary"
                onClick={() => submitBugReport(bugReportData)}
                disabled={submittingReport}
              >
                {submittingReport ? 'Submitting...' : 'Submit Report'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
}
